import dayjs from "dayjs";
import { PricingYearId } from "../model/PricingYearId";
import { getLocalApi } from "./localApi";
import apiConfig from "app/api/apiConfig.json";

export interface DivisionPricingYear {
    id: number;
    year: number;
    status?: string;
    state?: string;
    lifecycleStatus?: string;
    statusName?: string;
    isDraft?: boolean;
    isLocked?: boolean;
    pricingYearStartDate?: string;
    pricingYearEndDate?: string;
}

const mapPricingYearDate = (value: unknown): string | undefined => {
    if (value == null || value === "") {
        return undefined;
    }
    const parsed = dayjs(String(value).slice(0, 10));
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : undefined;
};

const mapPricingYearDateFields = (
    pricingYear: Record<string, unknown>
): Pick<PricingYearId, "pricingYearStartDate" | "pricingYearEndDate"> => {
    const pricingYearEndDate = mapPricingYearDate(
        pricingYear.pricingYearEndDate ??
        pricingYear.PricingYearEndDate ??
        pricingYear.allowedEndDate ??
        pricingYear.AllowedEndDate
    );
    const pricingYearStartDate = mapPricingYearDate(
        pricingYear.pricingYearStartDate ??
        pricingYear.PricingYearStartDate ??
        pricingYear.allowedStartDate ??
        pricingYear.AllowedStartDate
    );

    return {
        ...(pricingYearStartDate ? { pricingYearStartDate } : {}),
        ...(pricingYearEndDate ? { pricingYearEndDate } : {}),
    };
};

export const toPricingYearId = (
    py: DivisionPricingYear,
    divisionId: number
): PricingYearId => ({
    id: py.id,
    year: py.year,
    divisionId,
    isLocked: py.isLocked ?? false,
    ...(py.pricingYearStartDate ? { pricingYearStartDate: py.pricingYearStartDate } : {}),
    ...(py.pricingYearEndDate ? { pricingYearEndDate: py.pricingYearEndDate } : {}),
});
import { getDivisionId } from "app/shared/division/getDivisionId";

export async function getAllPricingYears(divisionId?: number) {
    const localApi = await getLocalApi();

    const template = (apiConfig as any).pricing_years_get_all as string | undefined;
    if (!template) {
        throw new Error("Pricing years endpoint is not configured.");
    }

    const divId = divisionId ?? getDivisionId();

    const path = template.includes("{divisionId}")
        ? template.replace("{divisionId}", encodeURIComponent(String(divId)))
        : template;

    return await localApi.fetchList(path,mapPricingYears, "Failed to fetch pricing years");
}

export async function getPricingYearsByDivision(divisionId: number) {
    const localApi = await getLocalApi();
    const template = (apiConfig as any).pricing_years_get_by_division as string | undefined;
    if (!template) {
        throw new Error('Pricing years by division endpoint is not configured.');
    }

    const path = template.replace('{divisionId}', encodeURIComponent(String(divisionId)));

    return await localApi.fetchList(path, mapDivisionPricingYears, 'Failed to fetch pricing years for the selected division');
}

function mapPricingYears(pricingYear: any): PricingYearId {
    const divisionId =
        typeof pricingYear.divisionId === 'object' && pricingYear.divisionId != null && 'value' in pricingYear.divisionId
            ? Number(pricingYear.divisionId.value)
            : Number(pricingYear.divisionId);
    return {
        id: pricingYear.id,
        year: pricingYear.year,
        divisionId: Number.isFinite(divisionId) ? divisionId : 0,
        isLocked:
            typeof pricingYear?.isLocked === 'boolean'
                ? pricingYear.isLocked
                : typeof pricingYear?.IsLocked === 'boolean'
                  ? pricingYear.IsLocked
                  : false,
        ...mapPricingYearDateFields(pricingYear),
    };
}

export function isDraftPricingYear(py: DivisionPricingYear): boolean {
    if (typeof py.isDraft === "boolean") {
        return py.isDraft;
    }
    const statusCandidates = [py.status, py.state, py.lifecycleStatus, py.statusName];
    return statusCandidates
        .filter((value): value is string => typeof value === "string")
        .some((status) => status.toLowerCase().includes("draft"));
}

function mapDivisionPricingYears(pricingYear: any): DivisionPricingYear {
    const statusCandidates = [
        pricingYear.status,
        pricingYear.state,
        pricingYear.lifecycleStatus,
        pricingYear.statusName,
    ];
    const normalizedStatus = statusCandidates.find((value: unknown) => typeof value === 'string') as string | undefined;
    const fromStatus = normalizedStatus ? normalizedStatus.toLowerCase().includes('draft') : false;
    const isDraft =
        typeof pricingYear.isDraftPricingYear === 'boolean'
            ? pricingYear.isDraftPricingYear
            : typeof pricingYear.isDraft === 'boolean'
                ? pricingYear.isDraft
                : fromStatus;

    const isLocked =
        typeof pricingYear?.isLocked === 'boolean'
            ? pricingYear.isLocked
            : typeof pricingYear?.IsLocked === 'boolean'
              ? pricingYear.IsLocked
              : false;

    return {
        id: pricingYear.id,
        year: pricingYear.year,
        status: pricingYear.status,
        state: pricingYear.state,
        lifecycleStatus: pricingYear.lifecycleStatus,
        statusName: pricingYear.statusName,
        isDraft,
        isLocked,
        ...mapPricingYearDateFields(pricingYear),
    };
}

export async function createPricingYear(divisionId: number, year: number): Promise<Response> {
    const localApi = await getLocalApi();
    const template = (apiConfig as any).pricing_years_create as string | undefined;
    if (!template) {
        throw new Error("Pricing years create endpoint is not configured.");
    }

    const path = template
        .replace("{divisionId}", encodeURIComponent(String(divisionId)))
        .replace("{year}", encodeURIComponent(String(year)));

    return await localApi.call(path, "PUT", null);
}